import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";

import { DocumentGroup } from "@/api";
import { ASSET_INVENTORY_UPLOAD_AREAS } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/assetInventoryUploadAreas";
import { useAssetInventoryMoves } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/hooks/useAssetInventoryMoves";
import { useAssetInventoryUpload } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/hooks/useAssetInventoryUpload";
import { usePendingMovesField } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/hooks/usePendingMovesField";
import { useTranslation } from "@/translations";

import { DocumentsOverviewFormValues } from "../DocumentsOverview.types";

export function useDocumentsOverview(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();

  const uploadDocuments = useAssetInventoryUpload(clientId);
  const moveDocuments = useAssetInventoryMoves(clientId);

  const initialValues: DocumentsOverviewFormValues = useMemo(
    () => ({
      uploads: Object.fromEntries(ASSET_INVENTORY_UPLOAD_AREAS.map((area) => [area.group, []])),
      documentMoves: [],
    }),
    []
  );

  const formik = useFormikWithDirtyForm<DocumentsOverviewFormValues>({
    initialValues,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      let uploadFailed = false;
      const remainingUploads: Partial<Record<DocumentGroup, File[]>> = {};
      for (const area of ASSET_INVENTORY_UPLOAD_AREAS) {
        const files = values.uploads[area.group] ?? [];
        const failed = files.length > 0 ? await uploadDocuments(area, files) : [];
        remainingUploads[area.group] = failed;
        uploadFailed = uploadFailed || failed.length > 0;
      }
      await helpers.setFieldValue("uploads", remainingUploads);

      const failedMoves = await moveDocuments(values.documentMoves);
      await helpers.setFieldValue("documentMoves", failedMoves);

      if (!uploadFailed && failedMoves.length === 0) {
        showSuccess(t("clients.client.debtSettlement.assetInventory.documentsOverview.successfullySaved"));
      }
    },
  });

  const { onAddMove, onCancelMove } = usePendingMovesField(formik);

  return { formik, onAddMove, onCancelMove };
}
